import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import type { ColDef } from "@/components/admin/shared/AdminTable";
import { AdminTable } from "@/components/admin/shared/AdminTable";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { DeleteConfirmModal } from "@/components/admin/shared/DeleteConfirmModal";
import { PageHeader, AddButton, SaveButton, CancelButton, ViewButton, EditButton, DeleteButton } from "@/components/admin/shared/PageHeader";
import { TablePagination } from "@/components/admin/shared/TablePagination";
import { TableSearch } from "@/components/admin/shared/TableSearch";
import { PennField, PennInput } from "@/components/ui";
import { useDebouncedSearch } from "@/hooks/useDebouncedSearch";
import { toolingSetSchema, type ToolingSetFormData } from "@/types/admin/toolingSets.types";
import {
  useToolingSets,
  useToolingSet,
  useCreateToolingSet,
  useUpdateToolingSet,
  useDeleteToolingSet,
} from "@/hooks/useToolingSets";
import type { ToolingSet } from "@/api/toolingSets";

type ModalMode = "add" | "edit" | "view";
type ModalState = { mode: ModalMode; item?: ToolingSet } | null;

const blank: ToolingSetFormData = {
  toolingSetId: "", lowerTool: "", upperTool: "",
  shuttle: "", multiModule: "", flightTube: "",
  toolingType: "", machineType: "", orientation: "", singulation: "",
};

const dash = (v?: string | null) => (v && v.trim() ? v : "—");

const apiErrorMessage = (err: unknown, fallback: string): string => {
  if (err && typeof err === "object" && "response" in err) {
    const data = (err as { response?: { data?: { error?: { message?: string } } } }).response?.data;
    if (data?.error?.message) return data.error.message;
  }
  return err instanceof Error ? err.message : fallback;
};

const orEmpty = (v: string | null | undefined): string => (v ?? "");
const orNull = (v: string | undefined): string | null => (v && v.trim() ? v.trim() : null);

export const ToolingSetsPage = () => {
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const { searchInput, setSearchInput, search, clearSearch } = useDebouncedSearch(() => setPageIndex(0));

  const params = useMemo(
    () => ({ page: pageIndex + 1, limit: pageSize, search: search || undefined }),
    [pageIndex, pageSize, search],
  );

  const { data, isLoading, isFetching, isError, error } = useToolingSets(params);
  const rows: ToolingSet[] = data?.data ?? [];
  const pagination = data?.pagination;
  const total = pagination?.total ?? 0;
  const hasNext = pagination ? pagination.page < pagination.totalPages : false;

  const [modal, setModal] = useState<ModalState>(null);
  const [deleteTarget, setDeleteTarget] = useState<ToolingSet | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const editingKey =
    modal?.mode !== "add" && modal?.item
      ? { toolingSetId: modal.item.toolingSetId, machineType: modal.item.machineType }
      : null;
  const detail = useToolingSet(editingKey);

  const createMutation = useCreateToolingSet();
  const updateMutation = useUpdateToolingSet();
  const deleteMutation = useDeleteToolingSet();

  const { register, handleSubmit, reset, formState: { errors } } = useForm<ToolingSetFormData>({
    resolver: zodResolver(toolingSetSchema),
  });

  const fillForm = (item: ToolingSet) => {
    reset({
      toolingSetId: item.toolingSetId,
      machineType: item.machineType,
      lowerTool: orEmpty(item.lowerTool),
      upperTool: orEmpty(item.upperTool),
      shuttle: orEmpty(item.shuttle),
      multiModule: orEmpty(item.multiModule),
      flightTube: orEmpty(item.flightTube),
      toolingType: orEmpty(item.toolingType),
      orientation: orEmpty(item.orientation),
      singulation: orEmpty(item.singulation),
    });
  };

  const openAdd = () => { setFormError(null); reset(blank); setModal({ mode: "add" }); };
  const openEdit = (item: ToolingSet) => { setFormError(null); fillForm(item); setModal({ mode: "edit", item }); };
  const openView = (item: ToolingSet) => { setFormError(null); fillForm(item); setModal({ mode: "view", item }); };
  const closeModal = () => { setModal(null); setFormError(null); };

  const onSubmit = (form: ToolingSetFormData) => {
    setFormError(null);
    const body = {
      toolingSetId: form.toolingSetId.trim(),
      machineType: form.machineType.trim(),
      lowerTool: orNull(form.lowerTool),
      upperTool: orNull(form.upperTool),
      shuttle: orNull(form.shuttle),
      multiModule: orNull(form.multiModule),
      flightTube: orNull(form.flightTube),
      toolingType: orNull(form.toolingType),
      orientation: orNull(form.orientation),
      singulation: orNull(form.singulation),
    };

    if (modal?.mode === "edit" && modal.item) {
      updateMutation.mutate(
        {
          oldKey: { toolingSetId: modal.item.toolingSetId, machineType: modal.item.machineType },
          body,
        },
        {
          onSuccess: closeModal,
          onError: (e) => setFormError(apiErrorMessage(e, "Failed to save tooling set.")),
        },
      );
    } else {
      createMutation.mutate(body, {
        onSuccess: closeModal,
        onError: (e) => setFormError(apiErrorMessage(e, "Failed to create tooling set.")),
      });
    }
  };

  const onConfirmDelete = () => {
    if (!deleteTarget) return;
    deleteMutation.mutate(
      { toolingSetId: deleteTarget.toolingSetId, machineType: deleteTarget.machineType },
      { onSettled: () => setDeleteTarget(null) },
    );
  };

  const isView = modal?.mode === "view";
  const saving = createMutation.isPending || updateMutation.isPending;

  const cols: ColDef<ToolingSet>[] = [
    { header: "ToolingSetId", key: "toolingSetId" },
    { header: "LowerTool", render: (i) => dash(i.lowerTool) },
    { header: "UpperTool", render: (i) => dash(i.upperTool) },
    { header: "Shuttle", render: (i) => dash(i.shuttle) },
    { header: "MultiModule", render: (i) => dash(i.multiModule) },
    { header: "FlightTube", render: (i) => dash(i.flightTube) },
    { header: "Actions", render: (i) => <div className="flex space-x-2"><ViewButton onClick={() => openView(i)} /><EditButton onClick={() => openEdit(i)} /><DeleteButton onClick={() => setDeleteTarget(i)} /></div> },
  ];

  const titleMap = {
    add: "Add Tooling Sets record",
    edit: "Edit Tooling Sets record",
    view: "View Tooling Sets record",
  };

  return (
    <>
      <PageHeader title="Dataset: Tooling Sets" subtitle={`Record-level editor for tooling sets. · ${total} records`} action={<AddButton onClick={openAdd} />} />

      {isError && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-body">
          Failed to load tooling sets{error instanceof Error ? `: ${error.message}` : "."}
        </div>
      )}

      <TableSearch
        value={searchInput}
        onChange={setSearchInput}
        onClear={clearSearch}
        placeholder="Search tooling set id…"
      />

      <AdminTable columns={cols} rows={rows} keyExtractor={(i) => `${i.toolingSetId}::${i.machineType}`} loading={isLoading} />

      <TablePagination
        pageIndex={pageIndex}
        pageSize={pageSize}
        rowsOnPage={rows.length}
        hasNext={hasNext}
        busy={isFetching}
        onPrev={() => setPageIndex((p) => Math.max(0, p - 1))}
        onNext={() => setPageIndex((p) => p + 1)}
        onPageSizeChange={(size) => { setPageSize(size); setPageIndex(0); }}
      />

      <AdminModal
        open={modal !== null}
        onClose={closeModal}
        title={modal ? titleMap[modal.mode] : ""}
        maxWidth="max-w-2xl"
        footer={
          isView
            ? <CancelButton onClick={closeModal} />
            : <><CancelButton onClick={closeModal} /><SaveButton onClick={handleSubmit(onSubmit)} label={saving ? "Saving…" : "Save"} /></>
        }
      >
        {modal?.mode !== "add" && detail.isLoading ? (
          <div className="py-10 text-center text-blue_grey4 text-body">Loading…</div>
        ) : (
          <form className="grid grid-cols-1 sm:grid-cols-2 gap-4" onSubmit={handleSubmit(onSubmit)}>
            {formError && (
              <div className="col-span-1 sm:col-span-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-red-700 text-body">
                {formError}
              </div>
            )}
            <PennField label="Tooling Set Id" htmlFor="toolingSetId" required={!isView} error={!isView ? errors.toolingSetId?.message : undefined}>
              <PennInput id="toolingSetId" disabled={isView} error={!isView && !!errors.toolingSetId} {...register("toolingSetId")} />
            </PennField>
            <PennField label="Lower Tool" htmlFor="lowerTool" required={!isView} error={!isView ? errors.lowerTool?.message : undefined}>
              <PennInput id="lowerTool" disabled={isView} error={!isView && !!errors.lowerTool} {...register("lowerTool")} />
            </PennField>
            <PennField label="Upper Tool" htmlFor="upperTool" required={!isView} error={!isView ? errors.upperTool?.message : undefined}>
              <PennInput id="upperTool" disabled={isView} error={!isView && !!errors.upperTool} {...register("upperTool")} />
            </PennField>
            <PennField label="Shuttle" htmlFor="shuttle" error={!isView ? errors.shuttle?.message : undefined}>
              <PennInput id="shuttle" disabled={isView} {...register("shuttle")} />
            </PennField>
            <PennField label="Multi Module" htmlFor="multiModule" error={!isView ? errors.multiModule?.message : undefined}>
              <PennInput id="multiModule" disabled={isView} {...register("multiModule")} />
            </PennField>
            <PennField label="Flight Tube" htmlFor="flightTube" error={!isView ? errors.flightTube?.message : undefined}>
              <PennInput id="flightTube" disabled={isView} {...register("flightTube")} />
            </PennField>
            <PennField label="Tooling Type" htmlFor="toolingType" error={!isView ? errors.toolingType?.message : undefined}>
              <PennInput id="toolingType" disabled={isView} {...register("toolingType")} />
            </PennField>
            <PennField label="Machine Type" htmlFor="machineType" required={!isView} error={!isView ? errors.machineType?.message : undefined}>
              <PennInput id="machineType" disabled={isView} error={!isView && !!errors.machineType} {...register("machineType")} />
            </PennField>
            <PennField label="Orientation" htmlFor="orientation" error={!isView ? errors.orientation?.message : undefined}>
              <PennInput id="orientation" disabled={isView} {...register("orientation")} />
            </PennField>
            <PennField label="Singulation" htmlFor="singulation" error={!isView ? errors.singulation?.message : undefined}>
              <PennInput id="singulation" disabled={isView} {...register("singulation")} />
            </PennField>
          </form>
        )}
      </AdminModal>

      <DeleteConfirmModal
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={onConfirmDelete}
        itemLabel={deleteTarget ? `${deleteTarget.toolingSetId} / ${deleteTarget.machineType}` : undefined}
      />
    </>
  );
};
